import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Gift, Ticket } from "lucide-react";
import { api } from "../lib/api";
import { Pill } from "./Home";

const POLL_MS = 2000;
const MAX_POLLS = 30;

const PaymentResult = () => {
  const location = useLocation();
  const isCancel = location.pathname.endsWith("/cancel");
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const [state, setState] = useState(isCancel ? "cancelled" : "checking");
  const [txn, setTxn] = useState(null);

  useEffect(() => {
    if (isCancel || !sessionId) {
      if (!isCancel && !sessionId) setState("error");
      return;
    }
    let polls = 0;
    let timer;
    const poll = async () => {
      polls += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTxn(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch {
        if (polls >= 3) { setState("error"); return; }
      }
      if (polls >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, isCancel]);

  return (
    <div className="bg-cream min-h-[60vh] flex items-center justify-center px-6 py-24" data-testid="payment-result">
      <div className="text-center max-w-xl">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-14 h-14 text-magenta mx-auto animate-spin" />
            <h1 className="font-serif-display uppercase text-plum-deep text-3xl sm:text-4xl mt-6">CONFIRMING YOUR PAYMENT…</h1>
            <p className="mt-3 text-plum-deep/70">Hold tight — this usually takes a few seconds.</p>
          </div>
        )}
        {state === "paid" && txn?.kind === "order" && (
          <div data-testid="payment-success-order">
            <CheckCircle2 className="w-16 h-16 text-forest mx-auto" />
            <h1 className="font-serif-display uppercase text-plum-deep text-4xl sm:text-5xl mt-6">PAYMENT CONFIRMED!</h1>
            <p className="mt-4 text-plum-deep/80 text-lg">
              Order <span className="font-semibold text-magenta" data-testid="paid-order-number">{txn.order_number}</span> is paid
              (${Number(txn.amount).toFixed(2)} NZD) and heading to our 3D printers. A receipt is on its way to your inbox.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <Pill to="/track" testid="payment-track-link">TRACK YOUR ORDER</Pill>
              <Pill to="/" testid="payment-home-link">BACK TO HOME</Pill>
            </div>
          </div>
        )}
        {state === "paid" && txn?.kind === "gift" && (
          <div data-testid="payment-success-gift">
            <Gift className="w-16 h-16 text-magenta mx-auto" />
            <h1 className="font-serif-display uppercase text-plum-deep text-4xl sm:text-5xl mt-6">GIFT SENT!</h1>
            <p className="mt-4 text-plum-deep/80 text-lg">Your ${Number(txn.amount).toFixed(2)} gift voucher has been emailed to your friend.</p>
            {txn.voucher_code && (
              <p className="mt-4 inline-flex items-center gap-2 bg-plum-deep text-lemon rounded-xl px-6 py-3 font-mono tracking-[0.25em] text-xl font-bold" data-testid="gift-voucher-code">
                <Ticket className="w-5 h-5" /> {txn.voucher_code}
              </p>
            )}
            <p className="mt-3 text-sm text-plum-deep/60">Keep this code as a backup — unused credit stays on it for next time.</p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <Pill to="/workshop" testid="payment-workshop-link">BACK TO THE WORKSHOP</Pill>
            </div>
          </div>
        )}
        {state === "cancelled" && (
          <div data-testid="payment-cancelled">
            <XCircle className="w-16 h-16 text-plum-deep/40 mx-auto" />
            <h1 className="font-serif-display uppercase text-plum-deep text-4xl mt-6">PAYMENT CANCELLED</h1>
            <p className="mt-4 text-plum-deep/70">No charge was made. Your order is saved — you can pay anytime from the order tracking page, or by bank transfer.</p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <Pill to="/track" testid="cancel-track-link">FIND MY ORDER</Pill>
              <Pill to="/workshop" testid="cancel-workshop-link">BACK TO THE WORKSHOP</Pill>
            </div>
          </div>
        )}
        {(state === "failed" || state === "error" || state === "timeout") && (
          <div data-testid="payment-problem">
            <XCircle className="w-16 h-16 text-magenta mx-auto" />
            <h1 className="font-serif-display uppercase text-plum-deep text-4xl mt-6">
              {state === "timeout" ? "STILL PROCESSING…" : "PAYMENT PROBLEM"}
            </h1>
            <p className="mt-4 text-plum-deep/70">
              {state === "timeout"
                ? "Your payment is taking longer than usual. If you were charged, we'll mark your order paid automatically — check back shortly."
                : "We couldn't confirm this payment. If you were charged, contact us and we'll sort it out."}
            </p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <Pill to="/track" testid="problem-track-link">CHECK MY ORDER</Pill>
              <Pill to="/contact" testid="problem-contact-link">CONTACT US</Pill>
            </div>
          </div>
        )}
        <Link to="/workshop" className="block mt-10 text-xs uppercase tracking-[0.2em] text-plum-deep/40 hover:text-magenta">← Back to workshop</Link>
      </div>
    </div>
  );
};

export default PaymentResult;
